import { connect } from 'react-redux';

import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { Button } from 'UIPackage/MainModule/components/Button';
import { IKnmPmFormExtension } from 'KnmPmPackage/PmExtensionModule/interfaces/IKnmPmFormExtension';
import { IKnmErknmFormEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEvent';
import { PmExtensionFileInput } from 'KnmPmPackage/PmExtensionModule/components/PmExtensionFileInput';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { EXTENSION_BLOCK_ID } from 'KnmPmPackage/MainModule/constants/knmPmFieldsElementsIds';
import { ProfKindCodesEnum } from 'APIPackage/DictionariesModule/enums/ProfKindCodesEnum';

import {
  getKnmFormRef,
  getKnmIsErknmStatusFillInProcess,
  getKnmIsErknmStatusTypeCarriedOut,
  getKnmIsErknmStatusTypeCompleted,
  getKnmIsErknmStatusTypeWaitingCarryOut,
} from '../../../KnmPackage/MainModule/selectors/knmSelectors';
import { IKnmFormRef } from '../../../KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import styles from './PmExtension.module.scss';

interface IPmExtensionProps {
  extension: IKnmPmFormExtension | undefined;
  events: IKnmErknmFormEvent[] | undefined;
  watchOnly: boolean;
  isUnableToRefuse: boolean | undefined;
  pmTypeCode?: ProfKindCodesEnum;
}

interface IPmExtensionStoreProps {
  formRef: IKnmFormRef | undefined;
  isKnmStatusFillInProcess: boolean;
  isKnmStatusTypeWaitingCarryOut: boolean;
  isKnmStatusTypeCarriedOut: boolean;
  isKnmStatusTypeCompleted: boolean;
}

function getConnectedEventValueAndName(
  events: IKnmErknmFormEvent[] | undefined,
): { value: IKnmErknmFormEvent | null; name: string | null } {
  let foundEvent: IKnmErknmFormEvent | null = null;
  let foundEventName: string | null = null;

  events?.forEach((event, index) => {
    if (event.type?.title === 'Инструментальное обследование') {
      foundEvent = event;
      foundEventName = `eventsPm[${index}]`;
    }
  });

  return {
    value: foundEvent,
    name: foundEventName,
  };
}

const PmExtensionWithoutStore = ({
  extension,
  events,
  watchOnly,
  isUnableToRefuse,
  formRef,
  isKnmStatusFillInProcess,
  isKnmStatusTypeCarriedOut,
  isKnmStatusTypeWaitingCarryOut,
  isKnmStatusTypeCompleted,
  pmTypeCode,
}: IPmExtensionProps & IPmExtensionStoreProps) => {
  const isNew = !extension?.guid;
  const name = 'periodExtension';

  const { value: connectedEvent, name: connectedEventName } = getConnectedEventValueAndName(events);

  const onAddExtension = () => {
    const newExtension: IKnmPmFormExtension = {
      isWorkDaysOnly: true,
    };

    formRef?.current.change(name as keyof IKnmForm, newExtension);
  };
  const onRemoveExtension = () => {
    formRef?.current.change(name as keyof IKnmForm);
  };

  if (
    !connectedEvent ||
    !connectedEventName ||
    !isUnableToRefuse ||
    !(
      isKnmStatusFillInProcess ||
      isKnmStatusTypeCarriedOut ||
      isKnmStatusTypeWaitingCarryOut ||
      isKnmStatusTypeCompleted
    )
  ) {
    return null;
  }

  /**
   * Доступно добавление если это Профилактический визит, не предусматривающий
   * возможность отказа, в статусе "Ожидает проведения", при наличии действия
   * "Инструментальное обследование" в перечне действий ПМ.
   * + существующая проверка на наличие продления
   */
  const isAddExtensionEnabled =
    pmTypeCode &&
    pmTypeCode === ProfKindCodesEnum.PP &&
    isUnableToRefuse &&
    isKnmStatusTypeWaitingCarryOut &&
    connectedEvent &&
    !extension;

  return (
    <FieldBlock
      label="Сведения о продлении срока проведения ПМ"
      margin="top"
      id={EXTENSION_BLOCK_ID}
      button={
        !watchOnly ? (
          <Button
            className="shared-btn-add"
            text="Добавить"
            type="button"
            variant="link"
            color="primary"
            onClick={onAddExtension}
            disabled={!isAddExtensionEnabled}
          />
        ) : undefined
      }
    >
      {extension && (
        <Row>
          <KnmCollapse
            title={`Решение о продлении № ${extension?.decisionNumber || ''}`}
            onClose={extension?.guid ? undefined : onRemoveExtension}
            isNew={isNew}
            opened={isNew}
          >
            <>
              <PmExtensionFileInput formRef={formRef} extension={extension} watchOnly={!isNew} parentFieldName={name} />
              <Row>
                <FieldBlock label="Действие" type="not-space" placementLabel="left" labelSize="col-4" color="black">
                  <SelectInputField
                    className="shared-row-field"
                    name={`${connectedEventName}.type`}
                    placeholder="Выберите действие"
                    size="medium"
                    disabled
                  />
                </FieldBlock>
              </Row>
              <Row>
                <FieldBlock
                  label="Дата и время решения"
                  placementLabel="left"
                  size="half"
                  type="not-space"
                  color="black"
                >
                  <DatePickerField
                    name={`${name}.decisionDateTime`}
                    placeholder="Выберите дату и время"
                    timeFormat
                    disabled={!isNew}
                    className="shared-row-field"
                  />
                </FieldBlock>
                <FieldBlock label="Номер решения" placementLabel="left" size="half" color="black">
                  <TextInputField
                    className="shared-row-flex"
                    name={`${name}.decisionNumber`}
                    placeholder="Введите номер"
                    disabled={!isNew}
                  />
                </FieldBlock>
              </Row>
              <Row align="center" notSpace>
                <FieldBlock label="Продление срока проведения ПМ" placementLabel="left" size="half" color="black">
                  <TextInputField
                    name={`${name}.amountDays`}
                    placeholder="Введите кол-во дней"
                    disabled={!isNew}
                    className="shared-row-field"
                  />
                </FieldBlock>
                <span className={styles.WorkDaysPostLabel}>рабочих дн.</span>
              </Row>
            </>
          </KnmCollapse>
        </Row>
      )}
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState): IPmExtensionStoreProps => ({
  formRef: getKnmFormRef(state),
  isKnmStatusFillInProcess: getKnmIsErknmStatusFillInProcess(state),
  isKnmStatusTypeWaitingCarryOut: getKnmIsErknmStatusTypeWaitingCarryOut(state),
  isKnmStatusTypeCompleted: getKnmIsErknmStatusTypeCompleted(state),
  isKnmStatusTypeCarriedOut: getKnmIsErknmStatusTypeCarriedOut(state),
});

export const PmExtension = connect(mapStateToProps)(PmExtensionWithoutStore);
